/**
 * NavBadgeCountsContext — las alertas sin leer, para las superficies del nav.
 *
 * Existe por una clase de bug concreta. `unreadCount` viajaba de <AppChrome/> a
 * las TRES pantallas que dibujan el globo de alertas —TopNav, BottomNav y
 * ReportesPage— como prop, y las tres la declaraban con default `= 0`. Un
 * refactor de rutas que perdiera uno de esos cableados —envolver el elemento de
 * una ruta, mover el render de la barra— dejaba ESE globo en cero para siempre:
 * sin error, sin warning y con la suite en verde, porque "cero alertas sin
 * leer" es un estado legítimo. Con tres superficies y un solo origen, el
 * cableado por prop era tres oportunidades de olvidarse.
 *
 * De ahí las dos guardas, que son el punto entero del archivo:
 *
 *   - el contexto arranca en `null` y `useNavBadgeCounts()` revienta si no hay
 *     proveedor arriba: la pantalla que se quede afuera falla ruidosa en el
 *     primer render en vez de dibujar un globo apagado;
 *   - el proveedor EXIGE `alerts`. Un default acá habría reproducido el bug un
 *     nivel más arriba: subir el provider sin arrastrar `unreadCount` apagaría
 *     los tres globos a la vez, en silencio.
 *
 * ─── Qué NO va acá ──────────────────────────────────────────────────────────
 *
 * El contador de pendientes se queda afuera, aunque desde el PR #658 ya no
 * cueste peticiones de más (`usePendingCount` lo sirve desde `resourceCache`,
 * con carga en vuelo compartida). La razón es otra: <AdminNavTree/> lo dibuja y
 * se renderiza FUERA de este proveedor. Meterlo acá obligaría a montar el
 * provider para pintar el rail de /admin suelto, a cambio de nada — el hook ya
 * es la fuente única.
 *
 * Las claves de este objeto alimentan los mapas de `badge` que cada superficie
 * arma a mano; `lib/__tests__/navConfig.test.js` ("cada árbol declara solo
 * claves que su superficie alimenta") vigila que ningún árbol declare una que
 * nadie sepa resolver.
 */
import { createContext, useContext, useMemo } from "react";

const NavBadgeCountsContext = createContext(null);

/**
 * @param {{alerts: number, children: React.ReactNode}} props
 *   alerts — alertas sin leer, de `useAlerts()` (instanciado una sola vez, en
 *   <AppChrome/>). OBLIGATORIO: ver arriba.
 */
export function NavBadgeCountsProvider({ alerts, children }) {
  const valido = Number.isFinite(alerts);
  const value = useMemo(() => ({ alerts: valido ? alerts : 0 }), [valido, alerts]);

  // Después del useMemo a propósito: un throw antes cambiaría la cantidad de
  // hooks entre renders.
  //
  // Y solo en desarrollo. Este proveedor envuelve el shell ENTERO —la barra, el
  // <Routes> completo y la barra inferior— y el <ErrorBoundary> está por encima
  // del router, así que reventar acá en producción no apaga un globo: reemplaza
  // toda la app por la pantalla de error. Es un precio absurdo por un contador
  // cosmético, sobre todo cuando el cableado que esto vigila ya lo caza
  // `__tests__/navBadgeCounts.guard.test.jsx` en CI, leyendo App.jsx. En dev y
  // en la suite revienta —que es donde tiene que doler—; en producción avisa
  // por consola (Sentry lo levanta como breadcrumb) y degrada a 0.
  if (!valido) {
    const mensaje =
      "<NavBadgeCountsProvider> necesita `alerts` numérico: sin él los tres " +
      "globos del nav quedarían en 0 en silencio, que es justo el bug que " +
      "este contexto vino a eliminar";
    if (import.meta.env.DEV) throw new Error(mensaje);
    console.error(mensaje, { alerts });
  }

  return (
    <NavBadgeCountsContext.Provider value={value}>
      {children}
    </NavBadgeCountsContext.Provider>
  );
}

/**
 * Lee los contadores del nav que vienen de arriba. Lanza si no hay
 * <NavBadgeCountsProvider/> — a propósito: un globo desconectado tiene que
 * doler, no mostrar 0.
 *
 * @returns {{alerts: number}}
 */
export function useNavBadgeCounts() {
  const ctx = useContext(NavBadgeCountsContext);
  if (!ctx) {
    throw new Error(
      "useNavBadgeCounts() necesita un <NavBadgeCountsProvider> por encima",
    );
  }
  return ctx;
}
